// The Kitchen Display's catering lane (kitchen flow, migration 150): each
// locked production plan as a ticket — its dishes to tick, the guests, and a
// countdown to the event. Shown when a cook taps the Catering chip; the order
// board itself is unchanged.
//
// Ticking a dish and marking a ticket done are the board's own state. They
// never change the plan's status or stock in Kitchen Production (the server
// guarantees it; the card says so).
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CalendarClock, CheckCircle, Flame, RefreshCw, Undo2, Users } from 'lucide-react'
import apiClient from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { RecipeCardButton } from '@/components/recipe-card/RecipeCardButton'
import { CATERING_KDS_QK } from '@/hooks/useCateringOnKds'
import { cn } from '@/lib/utils'
import { CK } from '@/lib/cateringKds/cateringKdsStrings'
import {
  allReady, dueInfo, longPressStatus, nextLineStatus, readyCount, splitTickets, ticketTitle, withLineStatus, withTicket,
  type DueTier,
} from '@/lib/cateringKds/cateringKdsLogic'
import type { CateringLine, CateringLineStatus, CateringTicket } from '@/lib/cateringKds/cateringKdsTypes'

type Notify = (kind: 'success' | 'error', message: string) => void

const TIER_STYLE: Record<DueTier, { card: string; due: string }> = {
  later: { card: 'border-border', due: 'text-muted-foreground' },
  soon: { card: 'border-amber-400 dark:border-amber-600', due: 'text-amber-700 dark:text-amber-300' },
  now: { card: 'border-orange-500 ring-2 ring-orange-400/40', due: 'text-orange-700 dark:text-orange-300' },
  late: { card: 'border-red-500 ring-2 ring-red-400/40', due: 'text-red-700 dark:text-red-300' },
}

/** Re-render once a minute so countdowns move without a refetch. */
function useMinuteTick(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60_000)
    return () => window.clearInterval(t)
  }, [])
  return now
}

function codeOf(e: unknown): string | undefined {
  return (e as { code?: string } | null)?.code
}

export function CateringBoard({ tickets, stationFilter, isLoading, isError, onRetry, daysBefore, hasRecipe, onOpenRecipe, notify }: {
  tickets: CateringTicket[]
  stationFilter: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  daysBefore: number
  hasRecipe: (productId: string) => boolean
  onOpenRecipe?: (ticket: CateringTicket, line: CateringLine) => void
  notify: Notify
}) {
  const qc = useQueryClient()
  const key = [...CATERING_KDS_QK, { stationFilter }] as const
  const now = useMinuteTick()

  const onWriteError = (e: unknown, fallback: string) => {
    const code = codeOf(e)
    notify('error', code === 'catering_ticket_not_ready' ? CK.notReady : code === 'catering_ticket_changed' || code === 'catering_ticket_closed' ? CK.ticketChanged : fallback)
    void qc.invalidateQueries({ queryKey: CATERING_KDS_QK })
  }
  const takeFresh = (fresh: CateringTicket | undefined) => {
    if (fresh) qc.setQueryData<CateringTicket[]>(key, (ts) => withTicket(ts, fresh))
  }

  const lineMutation = useMutation({
    mutationFn: (v: { planId: string; key: string; status: CateringLineStatus }) => apiClient.setCateringLineStatus(v.planId, v.key, v.status),
    onMutate: (v) => {
      // A refetch landing after this tick would undo it on screen; the
      // server's copy of the ticket (onSuccess) settles it either way.
      void qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<CateringTicket[]>(key)
      qc.setQueryData<CateringTicket[]>(key, (ts) => withLineStatus(ts, v.planId, v.key, v.status))
      return { prev }
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev)
      onWriteError(e, CK.lineFailed)
    },
    onSuccess: (res) => takeFresh(res.data),
  })
  const bumpMutation = useMutation({
    mutationFn: (planId: string) => apiClient.bumpCateringTicket(planId),
    onSuccess: (res) => takeFresh(res.data),
    onError: (e) => onWriteError(e, CK.lineFailed),
  })
  const recallMutation = useMutation({
    mutationFn: (planId: string) => apiClient.recallCateringTicket(planId),
    onSuccess: (res) => takeFresh(res.data),
    onError: (e) => onWriteError(e, CK.lineFailed),
  })

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (isError) {
    return (
      <div role="alert" className="m-3 flex flex-col gap-3 rounded-xl border border-danger-border bg-danger-surface p-4 sm:m-4 sm:flex-row sm:items-center">
        <p className="flex-1 text-sm font-semibold text-danger-fg">{CK.loadFailed}</p>
        <Button onClick={onRetry}>{CK.tryAgain}</Button>
      </div>
    )
  }

  const { active, done } = splitTickets(tickets)
  return (
    <div className="flex-1 overflow-y-auto p-3 sm:p-4">
      {active.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-1 py-16 text-center">
          <CalendarClock className="h-8 w-8 text-muted-foreground" aria-hidden />
          <p className="text-base font-medium">{CK.empty}</p>
          <p className="text-sm text-muted-foreground">{CK.emptyHelp(daysBefore)}</p>
        </div>
      ) : (
        <div className="grid content-start gap-3 grid-cols-[repeat(auto-fill,minmax(18rem,1fr))]">
          {active.map((t) => (
            <CateringTicketCard
              key={t.plan_id}
              ticket={t}
              now={now}
              hasRecipe={hasRecipe}
              onOpenRecipe={onOpenRecipe}
              onLine={(l, status) => lineMutation.mutate({ planId: t.plan_id, key: l.key, status })}
              onBump={() => bumpMutation.mutate(t.plan_id)}
              bumping={bumpMutation.isPending && bumpMutation.variables === t.plan_id}
            />
          ))}
        </div>
      )}

      {done.length > 0 && (
        <section className="mt-6 border-t border-dashed border-border pt-4" aria-labelledby="catering-done">
          <h2 id="catering-done" className="mb-1 flex items-center gap-2 text-base font-semibold">
            <CheckCircle className="h-5 w-5 text-emerald-600" aria-hidden />
            {CK.doneToday}
            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold tabular-nums text-secondary-foreground">{done.length}</span>
          </h2>
          <p className="mb-3 text-xs text-muted-foreground">{CK.doneNote}</p>
          <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {done.map((t) => (
              <li key={t.plan_id} className="flex items-center gap-3 rounded-lg border bg-card p-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{ticketTitle(t)}</p>
                  <p className="text-xs text-muted-foreground">{t.plan_no} · {CK.dishes(t.lines.length, t.lines.length)}</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={recallMutation.isPending && recallMutation.variables === t.plan_id}
                  onClick={() => recallMutation.mutate(t.plan_id)}
                >
                  <Undo2 className="h-4 w-4" aria-hidden /> {CK.recall}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

function CateringTicketCard({ ticket, now, hasRecipe, onOpenRecipe, onLine, onBump, bumping }: {
  ticket: CateringTicket
  now: Date
  hasRecipe: (productId: string) => boolean
  onOpenRecipe?: (ticket: CateringTicket, line: CateringLine) => void
  onLine: (line: CateringLine, status: CateringLineStatus) => void
  onBump: () => void
  bumping: boolean
}) {
  const due = dueInfo(ticket, now)
  const style = TIER_STYLE[due.tier]
  const ready = readyCount(ticket)
  const cancelled = Boolean(ticket.booking_cancelled)
  const canBump = allReady(ticket) && !cancelled
  return (
    <Card className={cn('flex flex-col overflow-hidden border-2', cancelled ? 'border-red-600 ring-2 ring-red-400/40' : style.card)}>
      <CardHeader className="space-y-1.5 border-b bg-purple-50/60 p-3 dark:bg-purple-950/20">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge className="border-transparent bg-purple-600 text-white hover:bg-purple-600">
            {ticket.source === 'catering' ? CK.badgeCatering : CK.badgePlan}
          </Badge>
          {ticket.plan_status === 'in_production' && (
            <Badge variant="outline" className="gap-1 border-amber-400 text-amber-800 dark:text-amber-300">
              <Flame className="h-3 w-3" aria-hidden /> {CK.cookingNow}
            </Badge>
          )}
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">{ticket.booking_no ?? ticket.plan_no}</span>
        </div>
        <h3 className="text-lg font-bold leading-tight">{ticketTitle(ticket)}</h3>
        {cancelled && (
          <div role="alert" className="rounded-lg bg-red-600 p-2 text-white">
            <p className="flex items-center gap-1.5 text-sm font-bold"><AlertTriangle className="h-4 w-4" aria-hidden /> {CK.bookingCancelled}</p>
            <p className="text-xs">{CK.bookingCancelledHelp}</p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {!cancelled && <span className={cn('flex items-center gap-1 font-semibold', style.due)}>
            <CalendarClock className="h-4 w-4" aria-hidden />
            {due.label}
            {ticket.event_time && due.tier !== 'late' ? <span className="font-normal text-muted-foreground">· {ticket.event_time}</span> : null}
          </span>}
          {ticket.guest_count != null && (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Users className="h-4 w-4" aria-hidden /> {CK.guests(ticket.guest_count)}
            </span>
          )}
        </div>
        {ticket.customer_note && (
          <div className="rounded-lg border border-amber-400 bg-amber-50 p-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
            <p className="flex items-center gap-1 text-xs font-bold uppercase"><AlertTriangle className="h-3.5 w-3.5" aria-hidden /> {CK.customerNote}</p>
            <p className="whitespace-pre-wrap font-semibold leading-snug">{ticket.customer_note}</p>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-2 p-3">
        <ul className="flex-1 space-y-1.5">
          {ticket.lines.map((l) => (
            <li key={l.key}>
              <CateringLineRow
                line={l}
                onToggle={() => onLine(l, nextLineStatus(l.status))}
                onLongPress={() => {
                  const s = longPressStatus(l.status)
                  if (s) onLine(l, s)
                }}
                recipe={onOpenRecipe && hasRecipe(l.product_id) ? () => onOpenRecipe(ticket, l) : undefined}
              />
            </li>
          ))}
        </ul>
        {!cancelled && <div className="space-y-1.5 border-t pt-2">
          <p className="text-center text-xs tabular-nums text-muted-foreground">{CK.dishes(ready, ticket.lines.length)}</p>
          <Button
            className={cn('w-full', canBump ? 'bg-orange-600 hover:bg-orange-700' : 'bg-border text-muted-foreground hover:bg-muted')}
            disabled={!canBump || bumping}
            onClick={onBump}
          >
            {bumping ? CK.marking : CK.done}
          </Button>
          {!canBump && <p className="text-center text-[11px] text-muted-foreground">{CK.tapHelp}</p>}
        </div>}
      </CardContent>
    </Card>
  )
}

/**
 * One dish: tap = ready ↔ not started, long press = cooking — the same
 * gestures as a till ticket's line. The Recipe button sits beside the row,
 * never inside it.
 */
function CateringLineRow({ line, onToggle, onLongPress, recipe }: {
  line: CateringLine
  onToggle: () => void
  onLongPress: () => void
  recipe?: () => void
}) {
  const timer = useRef<number | null>(null)
  const longPressed = useRef(false)
  const start = useRef<{ x: number; y: number } | null>(null)
  const cancel = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
  }
  const ready = line.status === 'ready'
  const cooking = line.status === 'preparing'
  const row = (
    <button
      type="button"
      onClick={() => {
        if (longPressed.current) {
          longPressed.current = false
          return
        }
        onToggle()
      }}
      onPointerDown={(e) => {
        longPressed.current = false
        start.current = { x: e.clientX, y: e.clientY }
        timer.current = window.setTimeout(() => {
          longPressed.current = true
          onLongPress()
        }, 450)
      }}
      // A finger that moves is scrolling the board, not pressing the dish.
      onPointerMove={(e) => {
        if (start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 10) cancel()
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={(e) => e.preventDefault()}
      aria-pressed={ready}
      className={cn(
        'w-full select-none touch-pan-y rounded-lg border p-2.5 text-left transition-colors',
        ready
          ? 'border-green-400 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
          : cooking
            ? 'border-amber-400 bg-amber-50 dark:border-amber-600 dark:bg-amber-900/25'
            : line.changed
              ? 'border-red-300 bg-red-50/60 dark:border-red-800 dark:bg-red-900/15'
              : 'border-border bg-card hover:border-amber-300',
      )}
    >
      <div className="flex items-start gap-2.5">
        <span
          className={cn(
            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border-2',
            ready ? 'border-green-500 bg-green-500 text-white' : cooking ? 'border-amber-500 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'border-input',
          )}
          aria-hidden
        >
          {ready ? <CheckCircle className="h-5 w-5" /> : cooking ? <Flame className="h-4 w-4" /> : null}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold leading-tight">
            <span className="text-lg font-black tabular-nums">{line.quantity}×</span> {line.product_name}
          </div>
          {line.option_labels.length > 0 && <p className="text-sm font-semibold leading-snug">{line.option_labels.join(', ')}</p>}
          {line.note && (
            <p className="mt-0.5 flex items-start gap-1 text-sm font-semibold leading-snug text-amber-800 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden /> {line.note}
            </p>
          )}
          {line.station_name && <p className="text-xs text-muted-foreground">{line.station_name}</p>}
          {line.changed && <p className="mt-0.5 text-xs font-semibold text-red-700 dark:text-red-300">{CK.changed}</p>}
        </div>
      </div>
    </button>
  )
  if (!recipe) return row
  return (
    <div className="flex items-start gap-1.5">
      <div className="min-w-0 flex-1">{row}</div>
      <RecipeCardButton iconOnly className="mt-0.5" name={line.product_name} onOpen={recipe} />
    </div>
  )
}
